import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/utils/cn"
import { motion } from "framer-motion"
import { Info } from "lucide-react"
import { Label } from "./label"

export type ThreeStateValue = "off" | "optimize" | "on"

interface ThreeStateToggleProps {
  id?: string
  label: string
  description?: string
  value: ThreeStateValue
  onChange: (value: ThreeStateValue) => void
  disabled?: boolean
}

interface StateOption {
  value: ThreeStateValue
  label: string
  shortLabel: string
  description: string
}

const stateOptions: StateOption[] = [
  {
    value: "off",
    label: "Always OFF",
    shortLabel: "OFF",
    description: "Parameter fixed at false - never tested during optimization",
  },
  {
    value: "optimize",
    label: "Optimize",
    shortLabel: "Optimize",
    description: "Optimizer will test both ON and OFF values to find the best setting",
  },
  {
    value: "on",
    label: "Always ON",
    shortLabel: "ON",
    description: "Parameter fixed at true - never tested during optimization",
  },
]

export function ThreeStateToggle({
  id,
  label,
  description,
  value,
  onChange,
  disabled = false,
}: ThreeStateToggleProps) {
  const selectedOption = stateOptions.find((opt) => opt.value === value) || stateOptions[1]

  return (
    <div className="space-y-3" data-testid={id ? `three-state-${id}` : undefined}>
      {/* Label and description */}
      <div className="flex items-center gap-2">
        <Label htmlFor={id} className="text-sm font-medium">
          {label}
        </Label>
        {description && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="w-4 h-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent className="bg-dark-800 text-white p-2 rounded-lg max-w-xs border border-dark-700">
                <p className="text-sm">{description}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Segmented control */}
      <div
        role="radiogroup"
        aria-labelledby={id}
        data-testid={id ? `radiogroup-${id}` : undefined}
        className={cn(
          "relative inline-flex rounded-lg bg-dark-800 p-1 border border-dark-700",
          disabled && "opacity-50 cursor-not-allowed"
        )}
      >
        {stateOptions.map((option) => {
          const isSelected = option.value === value
          const isOptimize = option.value === "optimize"

          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={disabled}
              onClick={() => !disabled && onChange(option.value)}
              className={cn(
                "relative z-10 px-4 py-2 text-sm font-medium transition-all duration-200",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 focus-visible:ring-offset-dark-900",
                "rounded-md min-w-[90px]",
                disabled && "cursor-not-allowed",
                !disabled && "cursor-pointer",
                isSelected
                  ? "text-white"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {/* Background gradient for selected state */}
              {isSelected && (
                <motion.div
                  layoutId={id ? `three-state-toggle-bg-${id}` : undefined}
                  className={cn(
                    "absolute inset-0 rounded-md",
                    isOptimize
                      ? "bg-gradient-to-r from-brand-blue to-brand-blue-dark"
                      : "bg-dark-700"
                  )}
                  transition={{
                    type: "spring",
                    stiffness: 500,
                    damping: 35,
                  }}
                />
              )}

              {/* Label text */}
              <span className="relative z-10">{option.shortLabel}</span>

              {/* "Fixed" badge for off/on states */}
              {isSelected && option.value !== "optimize" && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="relative z-10 ml-1.5 text-[10px] font-semibold uppercase tracking-wide opacity-70"
                >
                  Fixed
                </motion.span>
              )}
            </button>
          )
        })}
      </div>

      {/* Explanatory text showing current state */}
      {selectedOption && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          transition={{ duration: 0.2 }}
          className="text-xs text-muted-foreground leading-relaxed"
        >
          <span className="inline-flex items-center gap-1.5">
            <span className="font-medium text-brand-blue">
              {selectedOption.label}:
            </span>
            <span>{selectedOption.description}</span>
          </span>
        </motion.div>
      )}
    </div>
  )
}
